import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import { savedSearchApi } from '../../../../api/savedSearches';

import { useAuthOptions } from '../../../../contexts/UserContext';
import { useBrowserStorage } from '../../../../hooks/UseBrowserStorage';

import { formatAuthorizationHeader } from '../../../../helpers/auth';
import { rg4js } from '../../../../helpers/raygun';
import { PAGE } from '../../../../helpers/pages';
import { useFilterCount } from '../../../../components/SearchPage/hooks/useFilterCount/useFilterCount';
import { AUTHENTICATION_STATUSES } from '../../../../components/PageWrappers/AdDetails/types';

const useSavedSearchPrompt = () => {
  const { count } = useFilterCount();
  const { status, accessToken } = useAuthOptions();
  const { setLocalStorageItem, getLocalStorageItem } = useBrowserStorage();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isSavedSearchPromptOpen, setIsSavedSearchPromptOpen] = useState(false);

  const SAVED_SEARCH_PROMPT_EXPIRY_DATE_KEY =
    'displaySavedSearchPromptExpiryDate';
  const THIRTY_DAYS_IN_MILLISECONDS = 2592000000;
  const APPLIED_FILTER_THRESHOLD_FOR_DISPLAYING_MODAL = 2;

  const openToolTipAfterDelay = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(
      () => setIsSavedSearchPromptOpen(true),
      5000,
    );
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getSavedSearches = async (token: string) => {
    try {
      // Return the first result only if any
      const { data } = await savedSearchApi.getSavedSearches(
        {
          page: 0,
          offset: 1,
        },
        formatAuthorizationHeader(token),
      );

      return data;
    } catch (error) {
      rg4js('send', {
        error: new Error('Error fetching saved searches'),
        tags: [PAGE.SEARCH],
        customData: {
          message: error instanceof Error ? error.message : 'client_error',
        },
      });
    }
  };

  const handleTooltipDisplay = () => {
    if (count >= APPLIED_FILTER_THRESHOLD_FOR_DISPLAYING_MODAL) {
      const expiryDate = getLocalStorageItem(
        SAVED_SEARCH_PROMPT_EXPIRY_DATE_KEY,
      );
      const hasExpired = expiryDate ? parseInt(expiryDate) < Date.now() : true;
      if (hasExpired) {
        handleUserStatus();
      }
    }
  };

  const handleUserStatus = useCallback(async () => {
    if (status === AUTHENTICATION_STATUSES.UNAUTHENTICATED) {
      openToolTipAfterDelay();
    } else if (
      status === AUTHENTICATION_STATUSES.AUTHENTICATED &&
      accessToken
    ) {
      const savedSearches = await getSavedSearches(accessToken);
      if (savedSearches && savedSearches.length === 0) {
        openToolTipAfterDelay();
      }
    }
  }, [accessToken, status]);

  useMemo(handleTooltipDisplay, [count, handleUserStatus, getLocalStorageItem]);

  const closeSavedSearchPrompt = () => {
    setIsSavedSearchPromptOpen(false);
    setLocalStorageItem(
      SAVED_SEARCH_PROMPT_EXPIRY_DATE_KEY,
      (Date.now() + THIRTY_DAYS_IN_MILLISECONDS).toString(),
    );
  };

  return {
    isSavedSearchPromptOpen,
    closeSavedSearchPrompt,
  };
};

export { useSavedSearchPrompt };
